import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  CalendarClock,
  Plus,
  ChevronRight,
  ChevronDown,
  History,
  AlertTriangle,
  CheckCircle2,
  Wrench,
  Trash2,
  ClipboardList,
} from "lucide-react";
import api from "../../lib/api";
import FoldBlock from "./FoldBlock";
import Modal from "../Modal";
import DateTimePicker from "../DateTimePicker";
import BlurTextarea from "../BlurTextarea";

const ACTIONS = [
  { v: "serviced", label: "Serviced" },
  { v: "replaced", label: "Replaced" },
  { v: "inspected", label: "Inspected" },
  { v: "installed", label: "Installed" },
];

/**
 * Service Tracker Block
 * Lists each equipment piece (grouped by site) with its service-history dates as
 * a per-item dropdown. Shows Next-service date prominently. Each piece has a
 * "+" button to schedule a service; that opens a multi-equipment Service
 * Scheduler modal where the user picks a date and selects equipment + qty
 * to mark as serviced on that date.
 */
export default function ServiceTrackerBlock({
  equipment,
  serviceLogs,
  sites,
  jobcards = [],
  canEdit,
  onChanged,
  defaultOpen = false,
}) {
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const [seedEquipmentId, setSeedEquipmentId] = useState(null);

  const siteOf = (sid) => sites.find((s) => s.id === sid);

  // Group equipment by site
  const grouped = useMemo(() => {
    const g = {};
    equipment.forEach((eq) => {
      const k = eq.site_id || "_orphan";
      if (!g[k]) g[k] = [];
      g[k].push(eq);
    });
    return g;
  }, [equipment]);

  const sortedSiteIds = Object.keys(grouped).sort((a, b) => {
    const an = siteOf(a)?.name || "ZZ";
    const bn = siteOf(b)?.name || "ZZ";
    return an.localeCompare(bn);
  });

  // Logs per equipment id (sorted desc)
  const logsByEq = useMemo(() => {
    const m = {};
    serviceLogs.forEach((l) => {
      const k = l.site_equipment_id;
      if (!k) return;
      if (!m[k]) m[k] = [];
      m[k].push(l);
    });
    Object.values(m).forEach((arr) =>
      arr.sort((a, b) => new Date(b.service_date || 0) - new Date(a.service_date || 0)),
    );
    return m;
  }, [serviceLogs]);

  // Aggregate stats for the block header
  const overdueCount = equipment.filter(
    (eq) =>
      eq.is_service_item &&
      eq.next_service_date &&
      new Date(eq.next_service_date) < new Date(),
  ).length;

  const openScheduler = (seedId = null) => {
    setSeedEquipmentId(seedId);
    setSchedulerOpen(true);
  };

  return (
    <FoldBlock
      icon={CalendarClock}
      iconColor="#06b6d4"
      title="Service tracker"
      count={equipment.length}
      defaultOpen={defaultOpen}
      canEdit={canEdit}
      onAdd={() => openScheduler(null)}
      addLabel="Schedule service"
      testId="block-service-tracker"
      emptyHint="No equipment to track yet."
      headerExtra={
        overdueCount > 0 && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-rose-500/40 text-rose-300 bg-rose-500/10 flex items-center gap-1 font-bold">
            <AlertTriangle className="w-3 h-3" /> {overdueCount} overdue
          </span>
        )
      }
    >
      <div className="space-y-2">
        {sortedSiteIds.map((sid) => {
          const site = siteOf(sid);
          return (
            <SiteServiceGroup
              key={sid}
              siteName={site?.name || "Unassigned site"}
              items={grouped[sid]}
              logsByEq={logsByEq}
              jobcards={jobcards}
              canEdit={canEdit}
              onAddService={(eqId) => openScheduler(eqId)}
              onChanged={onChanged}
            />
          );
        })}
      </div>

      {schedulerOpen && (
        <ServiceSchedulerModal
          customerEquipment={equipment}
          sites={sites}
          jobcards={jobcards}
          seedEquipmentId={seedEquipmentId}
          onClose={() => setSchedulerOpen(false)}
          onSaved={() => {
            setSchedulerOpen(false);
            onChanged?.();
          }}
        />
      )}
    </FoldBlock>
  );
}

/* -------------------- Site group -------------------- */
function SiteServiceGroup({ siteName, items, logsByEq, jobcards, canEdit, onAddService, onChanged }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-white/5 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/[0.03] transition-colors"
        data-testid={`service-tracker-site-toggle`}
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="w-3 h-3 text-[#647087]" />
          ) : (
            <ChevronRight className="w-3 h-3 text-[#647087]" />
          )}
          <span className="text-sm font-bold">{siteName}</span>
          <span className="text-xs text-[#647087]">{items.length}</span>
        </div>
      </button>
      {open && (
        <div className="divide-y divide-white/5">
          {items.map((eq) => (
            <EquipmentServiceRow
              key={eq.id}
              eq={eq}
              logs={logsByEq[eq.id] || []}
              jobcards={jobcards}
              canEdit={canEdit}
              onAddService={() => onAddService(eq.id)}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- Per-equipment row -------------------- */
function EquipmentServiceRow({ eq, logs, jobcards, canEdit, onAddService, onChanged }) {
  const [open, setOpen] = useState(false);
  const next = eq.next_service_date ? new Date(eq.next_service_date) : null;
  const last = logs[0] ? new Date(logs[0].service_date) : null;
  const now = new Date();
  const overdue = next && next < now;
  const dueSoon = next && !overdue && (next - now) / (1000 * 60 * 60 * 24) < 30;

  return (
    <div className="bg-white/[0.01]" data-testid={`service-tracker-row-${eq.id}`}>
      <div
        className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer hover:bg-cyan-500/5 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {open ? (
            <ChevronDown className="w-3 h-3 text-[#647087] shrink-0" />
          ) : (
            <ChevronRight className="w-3 h-3 text-[#647087] shrink-0" />
          )}
          <Wrench className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{eq.name}</div>
            <div className="text-[10px] text-[#647087] truncate">
              {eq.quantity || 0} {eq.unit || "pcs"}
              {logs.length ? ` · ${logs.length} service${logs.length === 1 ? "" : "s"}` : " · never serviced"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Last service date chip */}
          {last && (
            <span
              className="hidden md:inline-flex text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-500/30 text-emerald-300 bg-emerald-500/5 items-center gap-1"
              title="Last serviced"
            >
              <CheckCircle2 className="w-2.5 h-2.5" />
              {last.toLocaleDateString('en-GB')}
            </span>
          )}
          {/* Next service date chip */}
          {next && (
            <span
              className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border whitespace-nowrap inline-flex items-center gap-1 ${
                overdue
                  ? "border-rose-500/40 text-rose-300 bg-rose-500/10"
                  : dueSoon
                    ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                    : "border-cyan-500/30 text-cyan-300 bg-cyan-500/5"
              }`}
              title={overdue ? "Overdue" : dueSoon ? "Due soon" : "Next service"}
            >
              <CalendarClock className="w-2.5 h-2.5" />
              {next.toLocaleDateString('en-GB')}
            </span>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAddService();
              }}
              className="hv-btn hv-btn-primary text-[10px] px-2 py-1"
              data-testid={`service-tracker-add-${eq.id}`}
              title="Schedule service"
            >
              <Plus className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-wider text-[#647087]">
            <History className="w-3 h-3" /> Service dates
          </div>
          {logs.length === 0 ? (
            <div className="text-xs text-[#647087] italic py-1">
              No service history yet.{" "}
              {canEdit && (
                <button
                  type="button"
                  onClick={onAddService}
                  className="text-[#1fb6c1] hover:underline"
                >
                  Log first service →
                </button>
              )}
            </div>
          ) : (
            <ul className="space-y-1" data-testid={`service-tracker-log-list-${eq.id}`}>
              {logs.map((log) => (
                <ServiceLogRow
                  key={log.id}
                  log={log}
                  jobcards={jobcards}
                  canEdit={canEdit}
                  onChanged={onChanged}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ServiceLogRow({ log, jobcards = [], canEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const linkedJc = log.jobcard_id
    ? jobcards.find((j) => j.id === log.jobcard_id)
    : null;
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/service-logs/${log.id}`);
      setConfirmOpen(false);
      onChanged?.();
    } catch {
      // best-effort: if endpoint doesn't allow, just close
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <li
      className="flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-white/5 bg-white/[0.02]"
      data-testid={`service-tracker-log-${log.id}`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
        <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
        <span className="text-xs font-mono text-[#dde3ec] shrink-0">
          {log.service_date ? new Date(log.service_date).toLocaleDateString('en-GB') : "—"}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-cyan-300 shrink-0">
          {log.action || "serviced"}
        </span>
        {log.quantity != null && (
          <span className="text-[10px] text-[#647087] shrink-0">
            · {log.quantity} {log.unit || ""}
          </span>
        )}
        {log.jobcard_id && (
          <Link
            to={`/jobcards/${log.jobcard_id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-indigo-500/40 text-indigo-300 bg-indigo-500/10 inline-flex items-center gap-1 hover:bg-indigo-500/20 transition-colors shrink-0"
            title={linkedJc?.title || "View job card"}
            data-testid={`service-tracker-log-jc-${log.id}`}
          >
            <ClipboardList className="w-2.5 h-2.5" />
            {linkedJc?.short_id ? `JC #${linkedJc.short_id}` : "Job card"}
          </Link>
        )}
        {log.notes && (
          <span className="text-xs text-[#a0aabf] truncate">· {log.notes}</span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {log.by_user_name && (
          <span className="text-[10px] text-[#647087]">{log.by_user_name}</span>
        )}
        {canEdit && !confirmOpen && (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="text-[#647087] hover:text-rose-300 transition-colors"
            title="Remove this service log"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
        {confirmOpen && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={del}
              disabled={busy}
              className="text-[10px] px-1.5 py-0.5 rounded border border-rose-500/40 text-rose-300 bg-rose-500/10"
            >
              {busy ? "…" : "Confirm"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
              className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-[#647087]"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

/* -------------------- Service Scheduler Modal (multi-equipment) -------------------- */
function ServiceSchedulerModal({
  customerEquipment,
  sites,
  jobcards = [],
  seedEquipmentId,
  onClose,
  onSaved,
}) {
  const initial = {};
  customerEquipment.forEach((eq) => {
    initial[eq.id] = {
      selected: eq.id === seedEquipmentId,
      quantity: eq.quantity || 1,
      action: "serviced",
    };
  });
  const [serviceDate, setServiceDate] = useState(new Date().toISOString());
  const [items, setItems] = useState(initial);
  const [notes, setNotes] = useState("");
  const [jobcardId, setJobcardId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [filterSiteId, setFilterSiteId] = useState(
    seedEquipmentId
      ? (customerEquipment.find((e) => e.id === seedEquipmentId)?.site_id || "all")
      : "all",
  );

  const siteOf = (sid) => sites.find((s) => s.id === sid);
  const visibleEq =
    filterSiteId === "all"
      ? customerEquipment
      : customerEquipment.filter((eq) => eq.site_id === filterSiteId);

  // group displayed equipment by site for nice readability
  const groupedDisplay = {};
  visibleEq.forEach((eq) => {
    const k = eq.site_id || "_";
    if (!groupedDisplay[k]) groupedDisplay[k] = [];
    groupedDisplay[k].push(eq);
  });

  const toggle = (id) =>
    setItems((s) => ({ ...s, [id]: { ...s[id], selected: !s[id].selected } }));
  const setQty = (id, qty) =>
    setItems((s) => ({ ...s, [id]: { ...s[id], quantity: qty } }));
  const setAct = (id, action) =>
    setItems((s) => ({ ...s, [id]: { ...s[id], action } }));

  const selectedIds = Object.entries(items)
    .filter(([, v]) => v.selected)
    .map(([k]) => k);

  const selectAllVisible = () => {
    setItems((s) => {
      const next = { ...s };
      visibleEq.forEach((eq) => {
        next[eq.id] = { ...next[eq.id], selected: true };
      });
      return next;
    });
  };
  const clearAll = () => {
    setItems((s) => {
      const next = { ...s };
      Object.keys(next).forEach((k) => (next[k] = { ...next[k], selected: false }));
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) {
      setErr("Select at least one equipment piece.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      // Fan out to /api/equipment/{eid}/service for each selected piece
      await Promise.all(
        selectedIds.map((id) => {
          const it = items[id];
          return api.post(`/equipment/${id}/service`, {
            service_date: serviceDate,
            action: it.action || "serviced",
            quantity: Number(it.quantity) || 0,
            notes,
            jobcard_id: jobcardId || null,
          });
        }),
      );
      onSaved?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not save service event");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Schedule service"
      testId="service-scheduler-modal"
      maxWidth="max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="service-scheduler-form">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
              Service date *
            </label>
            <DateTimePicker
              value={serviceDate}
              onChange={(v) => setServiceDate(v)}
              testId="service-scheduler-date"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
              Filter site
            </label>
            <select
              value={filterSiteId}
              onChange={(e) => setFilterSiteId(e.target.value)}
              className="hv-input mt-1"
              data-testid="service-scheduler-site-filter"
            >
              <option value="all">All sites</option>
              {Array.from(new Set(customerEquipment.map((e) => e.site_id)))
                .filter(Boolean)
                .map((sid) => (
                  <option key={sid} value={sid}>
                    {siteOf(sid)?.name || sid}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
              Equipment & quantities
            </label>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#647087]">
                {selectedIds.length} selected
              </span>
              <button
                type="button"
                onClick={selectAllVisible}
                className="text-[10px] text-[#1fb6c1] hover:underline"
                data-testid="service-scheduler-select-all"
              >
                Select all visible
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="text-[10px] text-[#647087] hover:text-white"
                data-testid="service-scheduler-clear-all"
              >
                Clear
              </button>
            </div>
          </div>
          <div className="border border-white/10 rounded-md max-h-[40vh] overflow-y-auto divide-y divide-white/5">
            {Object.keys(groupedDisplay).map((sid) => (
              <div key={sid}>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#647087] bg-white/[0.03]">
                  {siteOf(sid)?.name || "Unassigned"}
                </div>
                {groupedDisplay[sid].map((eq) => {
                  const it = items[eq.id] || { selected: false, quantity: eq.quantity || 1, action: "serviced" };
                  return (
                    <label
                      key={eq.id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-cyan-500/5 transition-colors ${
                        it.selected ? "bg-cyan-500/10" : ""
                      }`}
                      data-testid={`service-scheduler-item-${eq.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!it.selected}
                        onChange={() => toggle(eq.id)}
                        data-testid={`service-scheduler-checkbox-${eq.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{eq.name}</div>
                        <div className="text-[10px] text-[#647087] truncate">
                          on hand {eq.quantity || 0} {eq.unit || "pcs"}
                          {eq.next_service_date
                            ? ` · next ${new Date(eq.next_service_date).toLocaleDateString('en-GB')}`
                            : ""}
                        </div>
                      </div>
                      <select
                        value={it.action}
                        onChange={(e) => setAct(eq.id, e.target.value)}
                        disabled={!it.selected}
                        className="hv-input !w-auto !py-1 !text-xs disabled:opacity-50"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {ACTIONS.map((a) => (
                          <option key={a.v} value={a.v}>
                            {a.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={it.quantity}
                        onChange={(e) => setQty(eq.id, e.target.value)}
                        disabled={!it.selected}
                        className="hv-input !w-20 !py-1 !text-xs disabled:opacity-50"
                        data-testid={`service-scheduler-qty-${eq.id}`}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="text-[10px] text-[#647087] w-10 text-right">
                        {eq.unit || "pcs"}
                      </span>
                    </label>
                  );
                })}
              </div>
            ))}
            {visibleEq.length === 0 && (
              <div className="px-3 py-4 text-xs text-[#647087] italic text-center">
                No equipment for this site filter.
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf] flex items-center gap-1.5">
            <ClipboardList className="w-3 h-3" />
            Link job card <span className="text-[#647087] normal-case tracking-normal">(optional — applies to all selected)</span>
          </label>
          <select
            value={jobcardId}
            onChange={(e) => setJobcardId(e.target.value)}
            className="hv-input mt-1"
            data-testid="service-scheduler-jobcard"
          >
            <option value="">— No job card —</option>
            {jobcards
              .filter((j) => !["cancelled"].includes(j.phase))
              .sort((a, b) => (b.short_id || 0) - (a.short_id || 0))
              .map((j) => (
                <option key={j.id} value={j.id}>
                  {j.short_id ? `#${j.short_id} · ` : ""}
                  {j.title || "Untitled"}
                  {j.phase ? `  [${j.phase}]` : ""}
                </option>
              ))}
          </select>
          {jobcards.length === 0 && (
            <div className="text-[10px] text-[#647087] italic mt-1">
              No job cards on this site yet.
            </div>
          )}
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
            Notes (applies to all selected)
          </label>
          <BlurTextarea
            value={notes}
            onSave={setNotes}
            testid="service-scheduler-notes"
            placeholder="What was done? Replaced cartridges, sanitised, etc."
          />
        </div>

        {err && <div className="text-xs text-rose-300">{err}</div>}

        <div className="flex justify-end gap-2 pt-3 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || selectedIds.length === 0}
            className="hv-btn hv-btn-primary"
            data-testid="service-scheduler-submit"
          >
            <CheckCircle2 className="w-4 h-4" />{" "}
            {busy
              ? "Saving…"
              : `Log service for ${selectedIds.length || 0} item${selectedIds.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
